import React, {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectInUserProfile } from 'api/user';
import { PageLoading } from 'components/atoms/loading';
import { NotFound } from 'components/atoms/result';
import { Page } from 'components/molecules/page';
import { RoutePaths } from 'constant';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';
import { getProjectPlanUrl } from 'utils/urls';
import { RouterShellComponent } from '../router-shell';
import { Sidebar } from './sidebar';

const Main = styled(Page)`
  margin-left: 256px;
`;

const StyledPage = styled(Page)`
  align-items: center;
  justify-content: center;
`;

interface ProjectContext {
  project: ProjectInUserProfile | null;
  setProject: Dispatch<SetStateAction<null | ProjectInUserProfile>>;
}

export const ProjectContext = createContext<ProjectContext | null>(null);

export const useProjectContext = (): ProjectContext => {
  const context = useContext(ProjectContext);

  if (!context) {
    throw new Error(
      'This component must be used within a ProjectContext.Provider',
    );
  }

  return context;
};

export const CustomerPage: FC = ({ children }) => {
  const {
    projects,
    user: { company: customer },
    account: { accountName },
  } = useUser();

  const { project, setProject } = useProjectContext();

  useEffect(() => {
    if (!project && projects) {
      setProject(projects[0]);
    }
  }, [projects, project, setProject]);

  const { pathname } = useLocation();

  const { company: vendor } = useAccount();
  const history = useHistory();
  const handleOnSelect = (_projectId) => {
    history.push(getProjectPlanUrl(_projectId));
  };

  if (project) {
    return (
      <RouterShellComponent>
        <Sidebar
          projects={projects}
          onProjectChange={handleOnSelect}
          vendor={vendor}
          customer={customer}
          selectedItem={
            // small dirty little hack to show notification as selected menu item.
            pathname.includes(RoutePaths.notificationsSettings)
              ? RoutePaths.notifications
              : pathname
          }
          selectedProject={project}
        />
        <Main>{children}</Main>
      </RouterShellComponent>
    );
  }

  if (!projects.length) {
    return (
      <StyledPage>
        <NotFound
          title={<FormattedMessage id="project.no_access.error.404.title" />}
          subTitle={
            <>
              <FormattedMessage id="project.no_access.error.404.info" />
              <br />
              <FormattedMessage
                id="project.no_access.error.404.info_extra"
                values={{
                  vendorCompanyName: accountName,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </>
          }
          actionText={null}
        />
      </StyledPage>
    );
  }

  return <PageLoading />;
};
